"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyPlaceholder?: string;
  className?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select or type...",
  searchPlaceholder = "Search or type...",
  emptyPlaceholder = "No results. Type to add.",
  className,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  // Initialize inputValue with the label corresponding to the value, or the value itself
  const initialLabel = React.useMemo(() => {
    if (!value) return "";
    const selectedOption = options.find(opt => opt.value.toLowerCase() === value.toLowerCase());
    return selectedOption?.label || value; // Use label or the raw value if not in options
  }, [value, options]);
  const [inputValue, setInputValue] = React.useState(initialLabel);

  // Sync internal input value (label) if external value changes
  React.useEffect(() => {
    if (!value) {
      setInputValue("");
      return;
    }
    const selectedOption = options.find(opt => opt.value.toLowerCase() === value.toLowerCase());
    setInputValue(selectedOption?.label || value); // Set input to label or raw value
  }, [value, options]);

  const handleSelectOption = (selectedValue: string) => {
    // Find the canonical value from options (case-insensitive)
    const option = options.find(opt => opt.value.toLowerCase() === selectedValue.toLowerCase());
    const finalValue = option ? option.value : selectedValue; // Use canonical raw value or typed value
    const displayValue = option ? option.label : selectedValue; // Use label or typed value for display

    onChange(finalValue); // Notify parent with the raw value
    setInputValue(displayValue); // Update input display text to the label or typed value
    setOpen(false);
  };


  // Find display label for the button
   const displayLabel = React.useMemo(() => {
     if (!value) return placeholder;
     const selectedOption = options.find(opt => opt.value.toLowerCase() === value.toLowerCase());
     return selectedOption?.label || value; // Show label or the raw value if not in options
   }, [value, options, placeholder]);


  // Filter options based on input value (label) for display in the dropdown
  const filteredOptions = React.useMemo(() => {
      if (!inputValue) return options;
      return options.filter(option =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
  }, [inputValue, options]);


  return (
    <Popover 
      open={open} 
      onOpenChange={(isOpen) => {
        setOpen(isOpen); 
        if (isOpen) {
          // Reset input when opening
          setInputValue("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between", className)}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={inputValue}
            onValueChange={setInputValue} // Update input state as user types
            // Consider calling onChange here if immediate update is desired,
            // but usually selection or blur is better.
          />
          <CommandList>
             {/* Message when no options match */}
             {filteredOptions.length === 0 && inputValue && (
                 <div className="py-6 text-center text-sm">{emptyPlaceholder}</div>
             )}
            {/* Show "Add Category" option when input doesn't match existing label */}
            {inputValue && !options.some(opt => opt.label.toLowerCase() === inputValue.toLowerCase()) && (
                <CommandItem
                   key={inputValue} // Use input value as key for the "add" option
                   value={inputValue} // Use input value as value for selection
                   onSelect={() => handleSelectOption(inputValue)}
                >
                 <span className="mr-2 h-4 w-4"></span> {/* Placeholder for alignment */}
                  Add "{inputValue}"
                </CommandItem>
            )}
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value} // Use value for selection tracking
                  onSelect={() => handleSelectOption(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value?.toLowerCase() === option.value.toLowerCase() ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
} 